/**
 * @Author: wuyang
 * @Date: 2024/1/3
 * @Description: ""
 */

import { BaseAbstractClass } from '@/helper/base/BaseAbstractClass'
import { IRequestInstanceProps, IResponseData } from '@/helper/request/type'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { userinfoService } from '@/common/services/userinfo/userinfoService'

const downloadCache = new Map<string, Promise<any>>()





export class RequestInstance extends BaseAbstractClass<IRequestInstanceProps> {

	axiosInstance: AxiosInstance
	constructor(props: IRequestInstanceProps) {
		super(props)
		this.axiosInstance = axios.create({
			baseURL: props.baseUrl,
			timeout: 1000 * 60,
			maxContentLength: Infinity,
			maxBodyLength: Infinity,
			decompress: true,
		})
		this.init(props)
	}
	syncInit(props: IRequestInstanceProps) {
		super.syncInit(props)
		this.initInterceptors()
	}

	initInterceptors() {
		// 设置请求拦截器
		this.axiosInstance.interceptors.request.use(
			(config) => {
				config.headers.Authorization = 'Bearer ' + userinfoService.token.get()
				const contentType = config.headers['Content-Type']
				if (!contentType) {
					config.headers['Content-Type'] = 'application/json'
				}

				return config // 对config处理完后返回，下一步将向后端发送请求
			},
			(error) => {
				return Promise.reject(error)
			}
		)

		// 定义响应拦截器 -->token值无效时,清空token,并强制跳转登录页
		this.axiosInstance.interceptors.response.use(
			function (response) {
				// console.log(response,'response')
				if (response.status === 200) {
					// 如果是 blob 下载，直接成功
					if (response.config.responseType === 'blob') {
						return response.data
					}
					const { code, msg } = response.data
					if (code === 200) {
						return response.data.data
					} else if (code === 401) {
						userinfoService.logoutByOverTime()
						return Promise.reject(response)
					} else {
						return Promise.reject(response)
					}
				} else {
				}

				return Promise.reject(response)
			},
			function (error) {
				console.log('error报错', error)
				if (error.code === 'ERR_CANCELED') {
					return Promise.reject(error)
				} else {
					// console.log(error)
					// 响应状态码不是 2xx 时触发失败的回调，形参中的 error 是“失败的结果”
					if (error.response?.status === 401) {
						userinfoService.token.get() && userinfoService.token.set('')
						return
					} else {
						// message.warning(error.message)
						// messageApi.info('Hello, Ant Design!');
					}
					return Promise.reject(error)
				}
			}
		)
	}

	request<T extends any, R extends any>(
		params: AxiosRequestConfig<T>
	): { emit: () => Promise<R>; controller: AbortController } {
		const controller = new AbortController()

		const data = params.data
		const newData = Object.assign({}, data)

		return {
			emit: () => {
				return this.axiosInstance.request<R, R, T>({
					...params,
					data: newData,
					signal: controller.signal
				}) as Promise<R>
			},
			controller
		}
	}

	private async retryRequest<T>(requestFn: () => Promise<T>, maxRetries = 3, delay = 1000): Promise<T> {
		for (let i = 0; i <= maxRetries; i++) {
			try {
				return await requestFn()
			} catch (error: any) {
				if (i === maxRetries || error.code === 'ERR_CANCELED') {
					throw error
				}
				if (error.response?.status >= 400 && error.response?.status < 500) {
					throw error
				}
				await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)))
			}
		}
		throw new Error('Retry failed')
	}

	get<T extends any, R extends any>(url: string, data: T, useCache = true): IResponseData<R> {
		const headers: any = {}
		if (!useCache) {
			headers['Cache-Control'] = 'no-cache'
		}

		const { emit, controller } = this.request<T, R>({
			method: 'get',
			headers,
			url,
			params: data
		})
		return {
			fire: () => emit(),
			cancel: () => {
				controller.abort()
			}
		}
	}

	post<T extends any, R extends any>(url: string, data: T): IResponseData<R> {
		const { emit, controller } = this.request<T, R>({
			method: 'post',
			url,
			data
		})
		return {
			fire: () => emit(),
			cancel: () => {
				controller.abort()
			}
		}
	}

	del<T extends any, R extends any>(url: string, data: T): IResponseData<R> {
		const { emit, controller } = this.request<T, R>({
			method: 'delete',
			url,
			params: data
		})
		return {
			fire: () => emit(),
			cancel: () => {
				controller.abort()
			}
		}
	}
	put<T extends any, R extends any>(url: string, data: T): IResponseData<R> {
		const { emit, controller } = this.request<T, R>({
			method: 'put',
			url,
			data
		})
		return {
			fire: () => emit(),
			cancel: () => {
				controller.abort()
			}
		}
	}

	download<T extends any, R extends any>(url: string, params?: any, onProgress?: (progress: number) => void): IResponseData<R> {
		const cacheKey = `${url}?${JSON.stringify(params)}`

		const { emit, controller } = this.request<T, R>({
			method: 'get',
			url,
			params,
			responseType: 'blob',
			headers: {
				'Accept-Encoding': 'gzip, deflate, br'
			},
			onDownloadProgress: (progressEvent) => {
				if (onProgress && progressEvent.total) {
					const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
					onProgress(progress)
				}
			}
		})

		return {
			fire: () => {
				if (downloadCache.has(cacheKey)) {
					return downloadCache.get(cacheKey)!
				}

				const requestPromise = this.retryRequest(() => emit())
				downloadCache.set(cacheKey, requestPromise)

				requestPromise.catch(() => {
					downloadCache.delete(cacheKey)
				})

				return requestPromise
			},
			cancel: () => {
				controller.abort()
				downloadCache.delete(cacheKey)
			}
		}
	}

	downloadWithRange(
		url: string,
		params?: any,
		onProgress?: (progress: number) => void,
		chunkSize = 1024 * 1024 * 2
	): IResponseData<Blob> {
		const controller = new AbortController()

		return {
			fire: async (): Promise<Blob> => {
				try {
					const headResponse = await this.axiosInstance.head(url, { params })
					const fileSize = parseInt(String(headResponse.headers['content-length'] || '0'), 10)

					if (fileSize === 0 || !headResponse.headers['accept-ranges']) {
						return await this.download(url, params, onProgress).fire() as Blob
					}

					const chunks: ArrayBuffer[] = []
					let downloaded = 0

					for (let start = 0; start < fileSize; start += chunkSize) {
						const end = Math.min(start + chunkSize - 1, fileSize - 1)

						const chunkResponse = await this.retryRequest(() =>
							this.axiosInstance.get(url, {
								params,
								responseType: 'arraybuffer',
								headers: {
									'Range': `bytes=${start}-${end}`,
									'Accept-Encoding': 'gzip, deflate, br'
								},
								signal: controller.signal
							})
						)

						chunks.push(chunkResponse.data)
						downloaded += chunkResponse.data.byteLength

						if (onProgress) {
							onProgress(Math.round((downloaded / fileSize) * 100))
						}

						if (controller.signal.aborted) {
							throw new Error('Download aborted')
						}
					}

					return new Blob(chunks)
				} catch (error) {
					return await this.download(url, params, onProgress).fire() as Blob
				}
			},
			cancel: () => {
				controller.abort()
			}
		}
	}

	downloadPost<T extends any, R extends any>(url: string, data: T, onProgress?: (progress: number) => void): IResponseData<R> {
		const { emit, controller } = this.request<T, R>({
			method: 'post',
			url,
			data,
			responseType: 'blob',
			headers: {
				'Accept-Encoding': 'gzip, deflate, br'
			},
			onDownloadProgress: (progressEvent) => {
				if (onProgress && progressEvent.total) {
					const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
					onProgress(progress)
				}
			}
		})
		return {
			fire: () => emit(),
			cancel: () => {
				controller.abort()
			}
		}
	}

	upload<T extends any, R extends any>(url: string, data: T): IResponseData<R> {
		const { emit, controller } = this.request<T, R>({
			method: 'post',
			url,
			data,
			headers: { 'Content-Type': 'multipart/form-data' }
		})
		return {
			fire: () => emit(),
			cancel: () => {
				controller.abort()
			}
		}
	}
}
